import { type ReactNode } from "react";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Link from "@mui/material/Link";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import Typography from "@mui/material/Typography";
import FolderIcon from "@mui/icons-material/Folder";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { formatDateTime, formatFileSize } from "../../utils/format";
import { colorTokens } from "../../theme/tokens";
import type { SortColumn, SortDirection } from "../../hooks/useExplorerUrlState";
import { useRowMenu } from "../../hooks/useRowMenu";
import type { RowAction } from "../../utils/explorerActions";

export type { RowAction } from "../../utils/explorerActions";

export interface ExplorerRow {
  key: string;
  name: string;
  kind: "folder" | "file";
  /** ISO 8601, or null (a folder with no files has nothing to show here). */
  lastModified: string | null;
  /** Bytes, or null for a folder row (folders have no size, files.explorer-reference.png). */
  size: number | null;
  /** The name renders as a link to this URL (download), or with onActivate if set instead (folder navigation). */
  href?: string;
  onActivate?: () => void;
  /** An extra cell after Size, for example a status chip or a reason label. */
  extra?: ReactNode;
  /**
   * The context menu / "more" menu / Actions-dropdown items allowed for
   * this row, built by the caller from one shared per-role, per-folder,
   * per-status action model, so a row is never offered an action the
   * server would refuse. Omitted (or empty) rows get no "more" button and
   * do not respond to right-click, Shift+F10, the ContextMenu key or a
   * long-press.
   */
  actions?: RowAction[];
}

export interface ExplorerTableProps {
  caption: string;
  rows: ExplorerRow[];
  loading: boolean;
  emptyMessage: string;
  sort: SortColumn;
  dir: SortDirection;
  onSort: (column: SortColumn) => void;
  extraColumnHeader?: string;
  /** Selection checkboxes for the Actions toolbar (Download/Delete selected); omitted at the folder-list root view. */
  selectedKeys?: ReadonlySet<string>;
  onToggleSelect?: (key: string) => void;
  onToggleSelectAll?: () => void;
}

/**
 * The explorer's file/folder table (files-explorer-reference.png):
 * sortable Name/Last modified/Size columns (MUI TableSortLabel, which
 * keeps aria-sort in sync with the active column), a folder icon for
 * folder rows, file names as plain teal links, and a per-row contextual
 * menu (right-click, a visible "more" button, Shift+F10/ContextMenu on
 * the focused row, or a long-press on touch) built from `row.actions`.
 */
export function ExplorerTable({
  caption,
  rows,
  loading,
  emptyMessage,
  sort,
  dir,
  onSort,
  extraColumnHeader,
  selectedKeys,
  onToggleSelect,
  onToggleSelectAll,
}: ExplorerTableProps) {
  const selectable = Boolean(onToggleSelect);
  const allSelected =
    selectable && rows.length > 0 && rows.every((row) => selectedKeys?.has(row.key));
  const someSelected =
    selectable && rows.some((row) => selectedKeys?.has(row.key)) && !allSelected;
  const rowMenu = useRowMenu();

  if (loading) {
    return (
      <Stack sx={{ alignItems: "center", justifyContent: "center", py: 6 }}>
        <CircularProgress size={28} aria-label={`Loading ${caption}`} />
      </Stack>
    );
  }

  if (rows.length === 0) {
    return (
      <Box sx={{ py: 4, textAlign: "center" }}>
        <Typography color="text.secondary">{emptyMessage}</Typography>
      </Box>
    );
  }

  function headerFor(column: SortColumn, label: string, align?: "right") {
    return (
      <TableCell align={align} sortDirection={sort === column ? dir : false}>
        <TableSortLabel
          active={sort === column}
          direction={sort === column ? dir : "asc"}
          onClick={() => onSort(column)}
        >
          {label}
        </TableSortLabel>
      </TableCell>
    );
  }

  const menuRow = rowMenu.menu ? rows.find((row) => row.key === rowMenu.menu!.rowKey) : undefined;

  return (
    // Horizontal scroll, not wrapped cell text, on narrow screens: a
    // squeezed column keeps names and dates readable on one line.
    <Box sx={{ overflowX: "auto" }}>
      <Table aria-label={caption} size="small" sx={{ minWidth: 640 }}>
        <TableHead>
          <TableRow sx={{ bgcolor: colorTokens.bg }}>
            {selectable && (
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={someSelected}
                  checked={allSelected}
                  onChange={onToggleSelectAll}
                  slotProps={{ input: { "aria-label": "Select all rows" } }}
                />
              </TableCell>
            )}
            {headerFor("name", "Name")}
            {headerFor("lastModified", "Last modified")}
            {headerFor("size", "Size", "right")}
            {extraColumnHeader !== undefined && (
              <TableCell>{extraColumnHeader}</TableCell>
            )}
            <TableCell padding="checkbox" />
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow
              key={row.key}
              hover
              tabIndex={row.actions?.length ? 0 : undefined}
              onContextMenu={(event) => rowMenu.handleContextMenu(event, row.key, row.actions)}
              onKeyDown={(event) => rowMenu.handleKeyDown(event, row.key, row.actions)}
              onTouchStart={(event) => rowMenu.handleTouchStart(event, row.key, row.actions)}
              onTouchEnd={rowMenu.clearLongPress}
              onTouchMove={rowMenu.clearLongPress}
              onTouchCancel={rowMenu.clearLongPress}
            >
              {selectable && (
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={selectedKeys?.has(row.key) ?? false}
                    onChange={() => onToggleSelect?.(row.key)}
                    slotProps={{ input: { "aria-label": `Select ${row.name}` } }}
                  />
                </TableCell>
              )}
              <TableCell sx={{ whiteSpace: "nowrap" }}>
                <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                  {row.kind === "folder" && (
                    <FolderIcon fontSize="small" sx={{ color: colorTokens.textMuted }} />
                  )}
                  {row.onActivate ? (
                    <Link
                      component="button"
                      type="button"
                      underline="hover"
                      onClick={row.onActivate}
                    >
                      {row.name}
                    </Link>
                  ) : row.href ? (
                    <Link href={row.href} download underline="hover">
                      {row.name}
                    </Link>
                  ) : (
                    <Typography component="span" variant="body2">
                      {row.name}
                    </Typography>
                  )}
                </Stack>
              </TableCell>
              <TableCell>
                {row.lastModified ? formatDateTime(row.lastModified) : ""}
              </TableCell>
              <TableCell align="right">
                {row.size !== null ? formatFileSize(row.size) : ""}
              </TableCell>
              {extraColumnHeader !== undefined && <TableCell>{row.extra}</TableCell>}
              <TableCell padding="checkbox">
                {Boolean(row.actions?.length) && (
                  <IconButton
                    aria-label={`More actions for ${row.name}`}
                    size="small"
                    onClick={(event) => rowMenu.openAtElement(row.key, event.currentTarget)}
                  >
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>

        {rowMenu.menu && menuRow?.actions && (
          <Menu
            open
            onClose={rowMenu.close}
            anchorEl={rowMenu.menu.anchorEl ?? undefined}
            anchorReference={rowMenu.menu.anchorPosition ? "anchorPosition" : "anchorEl"}
            anchorPosition={rowMenu.menu.anchorPosition ?? undefined}
          >
            {menuRow.actions.map((action) => (
              <MenuItem
                key={action.key}
                onClick={() => {
                  rowMenu.close();
                  action.onSelect();
                }}
              >
                {action.label}
              </MenuItem>
            ))}
          </Menu>
        )}
      </Table>
    </Box>
  );
}
